// src/components/TuiEditor.jsx
import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Editor from '@toast-ui/editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { supabase } from '../lib/supabase';
import './TuiEditor.css';

function getBindingEventNames(props) {
  return Object.keys(props)
    .filter((key) => /^on[A-Z]/.test(key))
    .filter((key) => typeof props[key] === 'function');
}

function bindEventHandlers(editor, props) {
  getBindingEventNames(props).forEach((key) => {
    const eventName = key[2].toLowerCase() + key.slice(3);
    editor.off(eventName);
    editor.on(eventName, props[key]);
  });
}

export function transformContent(content) {
  if (!content) return '';

  const existingIframeIds = new Set();
  const iframeRegex =
    /<iframe[^>]+src="https:\/\/www\.youtube\.com\/embed\/([\w-]{11})"[^>]*><\/iframe>/g;
  let match;
  while ((match = iframeRegex.exec(content)) !== null) {
    existingIframeIds.add(match[1]);
  }

  const youtubeRegex =
    /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/)([\w-]{11})(?:[^\s<]*)?/g;
  content = content.replace(youtubeRegex, (_, videoId) => {
    if (existingIframeIds.has(videoId)) return `https://www.youtube.com/watch?v=${videoId}`;
    existingIframeIds.add(videoId);
    return `<iframe class="video-embed" src="https://www.youtube.com/embed/${videoId}" allowfullscreen></iframe>`;
  });

  const base64Regex = /!\[.*?\]\(data:image\/.*?;base64,.*?\)/g;
  content = content.replace(base64Regex, '');

  const imageRegex = /!\[(.*?)\]\(([^\s)]+)(\?[^)\s]*)?\)/g;
  content = content.replace(imageRegex, (_, alt, url) => `![${alt}](${url})`);

  // 3개 이상의 줄바꿈은 2개로 압축
  content = content.replace(/\n{3,}/g, '\n\n');

  return content.trim();
}

export function reverseTransformContent(content) {
  if (!content) return '';
  const iframeRegex =
    /<iframe[^>]+src="https:\/\/www\.youtube\.com\/embed\/([\w-]{11})"[^>]*><\/iframe>/g;
  return content.replace(iframeRegex, (_, videoId) => `https://www.youtube.com/watch?v=${videoId}`);
}

function allowIframeOnly(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('iframe').forEach((iframe) => {
    iframe.removeAttribute('sandbox');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('referrerpolicy', 'no-referrer');
    iframe.classList.add('video-embed');
  });

  // 표 셀에 클래스 부여 → CSS에서 통일 적용
  doc.querySelectorAll('th').forEach((th) => th.classList.add('custom-th'));
  doc.querySelectorAll('td').forEach((td) => td.classList.add('custom-td'));
  doc.querySelectorAll('.box-quote').forEach((quote) => quote.classList.add('custom-quote'));

  return doc.body.innerHTML;
}

const TuiEditor = forwardRef((props, ref) => {
  const placeholderRef = useRef(null);
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  useImperativeHandle(
    ref,
    () => ({
      getInstance: () => editorRef.current,
      getMarkdown: () => editorRef.current?.getMarkdown?.(),
      getHTML: () => editorRef.current?.getHTML?.(),
      setMarkdown: (markdown = '') => {
        const reversed = reverseTransformContent(markdown);
        const transformed = transformContent(reversed);
        editorRef.current?.setMarkdown?.(transformed || ' ');
      },
    }),
    []
  );

  useEffect(() => {
    const placeholder = placeholderRef.current;
    if (!placeholder?.parentNode || initializedRef.current) return;

    const height = props.height || '300px';
    const container = document.createElement('div');
    container.style.minHeight = height;
    placeholder.parentNode.insertBefore(container, placeholder);
    placeholder.style.display = 'none';

    const instance = new Editor({
      el: container,
      height,
      previewStyle: props.previewStyle || 'vertical',
      usageStatistics: false,
      initialEditType: props.initialEditType || 'markdown',
      customHTMLSanitizer: allowIframeOnly,
      hooks: {
        addImageBlobHook: async (blob, callback) => {
          if (blob.size === 0 || blob.type === 'image/svg+xml') return;
          if (blob.size > 1024 * 1024 * 20) return;
          const fileExt = blob.type.split('/')[1];
          const fileName = `post_${Date.now()}.${fileExt}`;
          const { error } = await supabase.storage.from('blog-images').upload(fileName, blob, {
            cacheControl: '3600',
            upsert: true,
          });
          if (error) return;
          const { data } = supabase.storage.from('blog-images').getPublicUrl(fileName);
          callback(data.publicUrl, 'image');
        },
      },
      ...props,
    });

    editorRef.current = instance;
    bindEventHandlers(instance, props);

    if (props.markdown !== undefined) {
      const reversed = reverseTransformContent(props.markdown);
      const transformed = transformContent(reversed);
      instance.setMarkdown(transformed || ' ');
    }

    initializedRef.current = true;

    return () => {
      editorRef.current?.destroy?.();
      editorRef.current = null;
      initializedRef.current = false;
      if (container.parentNode) container.parentNode.removeChild(container);
      if (placeholder) placeholder.style.display = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editorRef.current && typeof props.onChange === 'function') {
      editorRef.current.on('change', props.onChange);
    }
  }, [props.onChange]);

  const height = props.height || '300px';
  return <div ref={placeholderRef} style={{ minHeight: height }} aria-hidden="true" />;
});

export default TuiEditor;
